import {
    Component,
    computed,
    HostListener,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Fluid } from 'primeng/fluid';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import {
    Step,
    StepList,
    StepPanel,
    StepPanels,
    Stepper,
} from 'primeng/stepper';
import { Tag } from 'primeng/tag';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Toolbar } from 'primeng/toolbar';
import { MessageService } from 'primeng/api';
import {
    CategoryType,
    PaymentMethod,
    ProviderStatus,
    RealmBrandingConfig,
    RealmCategorySpecificConfigSetup,
    RealmConfig,
    RealmFineraConfig,
    RealmFineraWithdrawConfig,
} from '@/lib-backend';
import { RealmService } from '@/app/services/realm.service';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { CategoryService } from '@/app/services/category.service';
import { SiteContentService } from '@/app/services/site-content.service';
import { RealmGamesService } from '@/app/services/realm-games.service';
import { HasUnsavedChanges } from '@/app/guards/unsaved-changes.guard';
import { isSkillSpecific } from '@/app/utils/inheritance';
import { BrandingTab } from '../website-info/tabs/branding-tab';
import { PaymentTab } from '../website-info/tabs/payment-tab';
import { FaqTab } from '../website-info/tabs/faq-tab';
import { GamesTab } from '../website-info/tabs/games-tab';
import { SiteTemplateTab } from '../website-info/tabs/site-template-tab';

type CompanyMode = 'select' | 'create';

const STEP_BASICS = 1;
const STEP_COMPANY = 2;
const STEP_TEMPLATE = 3;
const STEP_BRANDING = 4;
const STEP_PAYMENT = 5;
const STEP_FAQ = 6;
const STEP_GAMES = 7;
const STEP_DONE = 8;

/**
 * Full-page onboarding wizard at /website/new, replacing the old Add Website
 * dialog. Steps 1-2 collect the minimal creation payload; the realm is created
 * on leaving step 2 (active but offline — the site serves 404 until a template
 * is assigned). Steps 3-7 progressively configure the just-created realm by
 * hosting the existing website-info tab components. Legal and Currency are
 * intentionally absent: they inherit from the category by default.
 */
@Component({
    selector: 'app-add-site-wizard',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Dialog,
        Fluid,
        InputText,
        Message,
        Select,
        Stepper,
        StepList,
        Step,
        StepPanels,
        StepPanel,
        Tag,
        Textarea,
        Toast,
        Toolbar,
        BrandingTab,
        PaymentTab,
        FaqTab,
        GamesTab,
        SiteTemplateTab,
    ],
    providers: [MessageService],
    templateUrl: './add-site-wizard.html',
})
export class AddSiteWizard implements OnInit, HasUnsavedChanges {
    private readonly router = inject(Router);
    private readonly messageService = inject(MessageService);
    protected readonly realmService = inject(RealmService);
    protected readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);
    protected readonly categoryService = inject(CategoryService);
    private readonly siteContentService = inject(SiteContentService);
    protected readonly gamesService = inject(RealmGamesService);

    protected readonly step = signal<number>(STEP_BASICS);

    protected readonly STEP_BASICS = STEP_BASICS;
    protected readonly STEP_COMPANY = STEP_COMPANY;
    protected readonly STEP_TEMPLATE = STEP_TEMPLATE;
    protected readonly STEP_BRANDING = STEP_BRANDING;
    protected readonly STEP_PAYMENT = STEP_PAYMENT;
    protected readonly STEP_FAQ = STEP_FAQ;
    protected readonly STEP_GAMES = STEP_GAMES;
    protected readonly STEP_DONE = STEP_DONE;

    // ── Step 1: basics ──────────────────────────────────────────────────────
    protected readonly domain = signal('');
    protected readonly displayName = signal('');
    protected readonly categoryId = signal<string | null>(null);
    /** Optional realm support email (RealmConfig.Support.SupportEmail). */
    protected readonly supportEmail = signal('');
    /** True once the operator edits the support email — stops domain auto-fill. */
    protected readonly supportEmailTouched = signal(false);
    protected readonly domainError = signal<string | null>(null);
    protected readonly displayNameError = signal<string | null>(null);
    protected readonly categoryError = signal<string | null>(null);
    protected readonly supportEmailError = signal<string | null>(null);

    protected readonly categoryOptions = computed(() =>
        this.categoryService.categories().map((c) => ({
            label: `${c.Name} (${c.Config.type})`,
            value: c.CategoryId,
        })),
    );

    // ── Step 2: company ─────────────────────────────────────────────────────
    protected readonly companyMode = signal<CompanyMode>('select');
    protected readonly selectedCompanyId = signal<string | null>(null);
    protected readonly newCompanyName = signal('');
    protected readonly newCompanyCountryCode = signal<string | null>(null);
    protected readonly newCompanyAddress = signal('');
    protected readonly newCompanyRegDetails = signal('');
    protected readonly companyError = signal<string | null>(null);
    protected readonly companyNameError = signal<string | null>(null);
    protected readonly companyCountryError = signal<string | null>(null);
    protected readonly companyAddressError = signal<string | null>(null);
    protected readonly companyRegError = signal<string | null>(null);

    protected readonly companyOptions = computed(() =>
        this.companyService
            .companies()
            .filter((c) => c.DeletedAt === null)
            .map((c) => {
                const country = this.countryService.getName(c.CountryCode);
                return {
                    label: country ? `${c.Name} — ${country}` : c.Name,
                    value: c.Id,
                };
            }),
    );
    protected readonly countryOptions: { label: string; value: string }[] =
        this.countryService
            .getAll()
            .map((c) => ({ label: `${c.name} (${c.code})`, value: c.code }));

    // ── Created realm (set when leaving step 2) ─────────────────────────────
    protected readonly realm = signal<RealmConfig | null>(null);
    protected readonly providerStatus = signal<ProviderStatus | null>(null);
    protected readonly creating = signal(false);
    protected readonly stepError = signal<string | null>(null);

    protected readonly isSkill = computed(
        () =>
            this.realm()?.CategorySpecific.type ===
            CategoryType.SkillBasedGames,
    );

    /** Wizard-local copy of the live/offline notion: live needs an assigned template. */
    protected readonly templateAssigned = computed(
        () => this.realm()?.SiteTemplateId != null,
    );

    // ── Step 4: branding draft ──────────────────────────────────────────────
    protected readonly brandingDraft =
        signal<RealmBrandingConfig>(emptyBranding());
    private readonly brandingOriginal =
        signal<RealmBrandingConfig>(emptyBranding());
    protected readonly brandingDirty = computed(
        () =>
            JSON.stringify(this.brandingDraft()) !==
            JSON.stringify(this.brandingOriginal()),
    );

    // ── Step 5: payment drafts ──────────────────────────────────────────────
    protected readonly fineraDraft = signal<RealmFineraConfig>(emptyFinera());
    protected readonly fineraWithdrawDraft = signal<RealmFineraWithdrawConfig>(
        emptyFineraWithdraw(),
    );
    protected readonly paymentMethodDraft = signal<PaymentMethod>(
        PaymentMethod.None,
    );
    private readonly fineraOriginal = signal<RealmFineraConfig>(emptyFinera());
    private readonly fineraWithdrawOriginal = signal<RealmFineraWithdrawConfig>(
        emptyFineraWithdraw(),
    );
    private readonly paymentMethodOriginal = signal<PaymentMethod>(
        PaymentMethod.None,
    );
    protected readonly paymentDirty = computed(
        () =>
            JSON.stringify(this.fineraDraft()) !==
                JSON.stringify(this.fineraOriginal()) ||
            (this.isSkill() &&
                JSON.stringify(this.fineraWithdrawDraft()) !==
                    JSON.stringify(this.fineraWithdrawOriginal())) ||
            this.paymentMethodDraft() !== this.paymentMethodOriginal(),
    );

    protected readonly saving = signal(false);

    // ── Done-step summary ───────────────────────────────────────────────────
    protected readonly faqCount = computed(
        () => this.siteContentService.faqItems().length,
    );
    protected readonly gamesCount = computed(
        () => this.gamesService.games().length,
    );
    protected readonly categoryName = computed(() => {
        const id = this.categoryId();
        return (
            this.categoryService.categories().find((c) => c.CategoryId === id)
                ?.Name ?? ''
        );
    });
    protected readonly companyName = computed(() => {
        const r = this.realm();
        if (!r) return '';
        return this.companyService.byId().get(r.CompanyId)?.Name ?? '';
    });

    // ── Leave-confirmation dialog (unsaved-changes guard) ───────────────────
    protected readonly leaveDialogVisible = signal(false);
    private leaveResolve: ((value: boolean) => void) | null = null;

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
        }
    }

    ngOnInit(): void {
        this.companyService.loadCompanies();
        this.categoryService.loadCategories();
        // Realms feed the FAQ import-source dropdown on step 6.
        this.realmService.loadRealms();
    }

    hasUnsavedChanges(): boolean {
        if (this.step() === STEP_DONE) return false;
        if (!this.realm()) {
            return (
                this.domain().trim().length > 0 ||
                this.displayName().trim().length > 0
            );
        }
        return this.brandingDirty() || this.paymentDirty();
    }

    showUnsavedDialog(): Promise<boolean> {
        this.leaveDialogVisible.set(true);
        return new Promise<boolean>((resolve) => {
            this.leaveResolve = resolve;
        });
    }

    protected onLeaveConfirm(): void {
        this.leaveDialogVisible.set(false);
        this.leaveResolve?.(true);
        this.leaveResolve = null;
    }

    protected onLeaveCancel(): void {
        this.leaveDialogVisible.set(false);
        this.leaveResolve?.(false);
        this.leaveResolve = null;
    }

    protected exit(): void {
        // Router guard shows the leave dialog when there is unsaved work.
        this.router.navigate(['/website']);
    }

    // ── Step 1 → 2 ──────────────────────────────────────────────────────────

    /** Auto-fill the support email as support@<domain> until the operator edits it. */
    protected setDomain(value: string): void {
        this.domain.set(value);
        if (!this.supportEmailTouched()) {
            const d = value.trim();
            this.supportEmail.set(d ? `support@${d}` : '');
        }
    }

    protected setSupportEmail(value: string): void {
        this.supportEmail.set(value);
        // Manual entry pins the value; clearing it resumes the domain auto-fill.
        this.supportEmailTouched.set(!!value.trim());
    }

    protected basicsNext(): void {
        this.domainError.set(null);
        this.displayNameError.set(null);
        this.categoryError.set(null);
        this.supportEmailError.set(null);

        let invalid = false;
        if (!this.domain().trim()) {
            this.domainError.set('Domain is required');
            invalid = true;
        }
        if (!this.displayName().trim()) {
            this.displayNameError.set('Display name is required');
            invalid = true;
        }
        if (!this.categoryId()) {
            this.categoryError.set('Select a category');
            invalid = true;
        }
        // Support email is optional; validate only when provided.
        const support = this.supportEmail().trim();
        if (support && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(support)) {
            this.supportEmailError.set('Enter a valid email address');
            invalid = true;
        }
        if (invalid) return;
        this.step.set(STEP_COMPANY);
    }

    protected setCompanyMode(mode: CompanyMode): void {
        this.companyMode.set(mode);
        this.clearCompanyErrors();
    }

    private clearCompanyErrors(): void {
        this.companyError.set(null);
        this.companyNameError.set(null);
        this.companyCountryError.set(null);
        this.companyAddressError.set(null);
        this.companyRegError.set(null);
        this.stepError.set(null);
    }

    protected backToBasics(): void {
        if (this.creating()) return;
        this.step.set(STEP_BASICS);
    }

    // ── Step 2 → 3: create the realm ────────────────────────────────────────

    protected async createAndContinue(): Promise<void> {
        if (this.creating()) return;
        if (this.realm()) {
            // Already created on a previous attempt; just move on.
            this.step.set(STEP_TEMPLATE);
            return;
        }
        this.clearCompanyErrors();

        const category = this.categoryService
            .categories()
            .find((c) => c.CategoryId === this.categoryId());
        if (!category) {
            this.categoryError.set('Selected category is no longer available');
            this.step.set(STEP_BASICS);
            await this.categoryService.loadCategories();
            return;
        }

        let companyId: string | null;
        if (this.companyMode() === 'select') {
            companyId = this.selectedCompanyId();
            if (!companyId) {
                this.companyError.set('Select a company');
                return;
            }
        } else {
            companyId = await this.createInlineCompany();
            if (!companyId) return;
        }

        this.creating.set(true);
        try {
            const displayName = this.displayName().trim();
            const branding: RealmBrandingConfig = {
                ...emptyBranding(),
                Name: displayName,
                FullName: displayName,
                DisplayName: displayName,
            };
            // New realms inherit all category-specific settings (overrides null).
            const categorySpecific: RealmCategorySpecificConfigSetup =
                category.Config.type === CategoryType.SkillBasedGames
                    ? {
                          type: CategoryType.SkillBasedGames,
                          KycOverride: null,
                          WithdrawOverride: null,
                          CurrencyOverride: null,
                          LegalOverride: null,
                          FineraWithdraw: emptyFineraWithdraw(),
                      }
                    : {
                          type: CategoryType.LuckyBasedGames,
                          KycOverride: null,
                          CurrencyOverride: null,
                          LegalOverride: null,
                      };
            const result = await this.realmService.addRealm({
                Domain: this.domain().trim(),
                CompanyId: companyId,
                Branding: branding,
                Support: { SupportEmail: this.supportEmail().trim() },
                Google: { IsSignUpEnabled: false, ClientId: '' },
                Finera: emptyFinera(),
                ActivePaymentMethod: PaymentMethod.None,
                CategoryId: category.CategoryId,
                CategorySpecific: categorySpecific,
            });

            if (result.result === 'Ok' && result.realmId) {
                await this.adoptRealm(result.realmId);
                this.step.set(STEP_TEMPLATE);
                return;
            }

            if (this.companyMode() === 'create') {
                // The company was created; don't create it again on retry.
                this.companyMode.set('select');
                this.selectedCompanyId.set(companyId);
            }

            switch (result.result) {
                case 'DomainAlreadyUsed':
                    this.domainError.set('This domain is already in use');
                    this.step.set(STEP_BASICS);
                    break;
                case 'InvalidDomain':
                    this.domainError.set('Invalid domain format');
                    this.step.set(STEP_BASICS);
                    break;
                case 'CategoryNotFound':
                case 'CategoryTypeMismatch':
                    this.categoryError.set(
                        'Selected category is no longer available. Pick another.',
                    );
                    this.step.set(STEP_BASICS);
                    await this.categoryService.loadCategories();
                    break;
                case 'CompanyNotFound':
                case 'CompanyDeleted':
                    this.companyError.set(
                        'Selected company is no longer available. Pick another.',
                    );
                    await this.companyService.loadCompanies();
                    break;
                default:
                    this.stepError.set('Failed to create website');
            }
        } finally {
            this.creating.set(false);
        }
    }

    private async createInlineCompany(): Promise<string | null> {
        const name = this.newCompanyName().trim();
        const country = this.newCompanyCountryCode();
        const address = this.newCompanyAddress().trim();
        const reg = this.newCompanyRegDetails().trim();

        let invalid = false;
        if (!name) {
            this.companyNameError.set('Name is required');
            invalid = true;
        }
        if (!country) {
            this.companyCountryError.set('Country is required');
            invalid = true;
        }
        if (!address) {
            this.companyAddressError.set('Address is required');
            invalid = true;
        }
        if (!reg) {
            this.companyRegError.set('Registration details are required');
            invalid = true;
        }
        if (invalid) return null;

        this.creating.set(true);
        try {
            const outcome = await this.companyService.createCompany({
                Name: name,
                Address: address,
                RegistrationDetails: reg,
                Country: country!,
                Stripe: {
                    IsEnabled: false,
                    PublishableKey: '',
                    SecretKey: '',
                    WebhookSecret: '',
                },
                LiveChatConfig: { GroupId: null },
            });
            if (outcome.result === 'Ok') return outcome.company.Id;
            if (outcome.result === 'NameAlreadyUsed') {
                this.companyNameError.set(
                    'A company with this name already exists',
                );
            } else {
                this.stepError.set('Failed to create company');
            }
            return null;
        } finally {
            this.creating.set(false);
        }
    }

    /** Fetch the freshly created realm and seed all step drafts from it. */
    private async adoptRealm(realmId: string): Promise<void> {
        const loaded = await this.realmService.getRealm(realmId);
        if (!loaded) {
            this.stepError.set(
                'Website was created but could not be loaded. Open it from the list.',
            );
            return;
        }
        this.applyRealm(loaded.config);
        this.providerStatus.set(loaded.providerStatus);
        this.siteContentService.loadFaqsForRealm(realmId);
    }

    private applyRealm(config: RealmConfig): void {
        this.realm.set(config);
        this.seedDrafts(config);
    }

    private seedDrafts(config: RealmConfig): void {
        const branding = { ...config.Branding };
        const finera = {
            ...config.Finera,
            Deposit: { ...config.Finera.Deposit },
        };
        const fineraWithdraw = isSkillSpecific(config.CategorySpecific)
            ? clone(config.CategorySpecific.FineraWithdraw)
            : emptyFineraWithdraw();

        this.brandingDraft.set(branding);
        this.brandingOriginal.set(clone(branding));
        this.fineraDraft.set(finera);
        this.fineraOriginal.set(clone(finera));
        this.fineraWithdrawDraft.set(fineraWithdraw);
        this.fineraWithdrawOriginal.set(clone(fineraWithdraw));
        this.paymentMethodDraft.set(config.ActivePaymentMethod);
        this.paymentMethodOriginal.set(config.ActivePaymentMethod);
    }

    /** SiteTemplateTab applied an assignment and returned the updated config. */
    protected onRealmUpdated(config: RealmConfig): void {
        // Keep in-progress branding/payment drafts; only the config changes.
        this.realm.set(config);
    }

    // ── Steps 4-5: save & continue ──────────────────────────────────────────

    protected async saveBrandingAndContinue(): Promise<void> {
        if (!(await this.savePartial({ branding: true }))) return;
        this.step.set(STEP_PAYMENT);
    }

    protected async savePaymentAndContinue(): Promise<void> {
        if (!(await this.savePartial({ payment: true }))) return;
        this.step.set(STEP_FAQ);
    }

    private async savePartial(parts: {
        branding?: boolean;
        payment?: boolean;
    }): Promise<boolean> {
        const realm = this.realm();
        if (!realm) return false;

        const sendBranding = (parts.branding ?? false) && this.brandingDirty();
        const sendPayment = (parts.payment ?? false) && this.paymentDirty();
        if (!sendBranding && !sendPayment) return true;

        const fineraWithdrawDirty =
            this.isSkill() &&
            JSON.stringify(this.fineraWithdrawDraft()) !==
                JSON.stringify(this.fineraWithdrawOriginal());

        this.saving.set(true);
        this.stepError.set(null);
        try {
            const result = await this.realmService.updateRealm({
                RealmId: realm.RealmId,
                Branding: sendBranding ? this.brandingDraft() : null,
                Support: null,
                Google: null,
                Finera: sendPayment ? this.fineraDraft() : null,
                ActivePaymentMethod: sendPayment
                    ? this.paymentMethodDraft()
                    : null,
                Category:
                    sendPayment &&
                    fineraWithdrawDirty &&
                    isSkillSpecific(realm.CategorySpecific)
                        ? {
                              type: CategoryType.SkillBasedGames,
                              KycOverride: null,
                              WithdrawOverride: null,
                              CurrencyOverride: null,
                              LegalOverride: null,
                              FineraWithdraw: this.fineraWithdrawDraft(),
                          }
                        : null,
            });
            if (result === 'InvalidActivePaymentMethod') {
                this.stepError.set(
                    'Selected payment method is not configured. Enable it first.',
                );
                return false;
            }
            if (result !== 'Ok') {
                this.stepError.set(`Save failed: ${result}`);
                return false;
            }
            const updated = await this.realmService.getRealm(realm.RealmId);
            if (updated) {
                this.applyRealm(updated.config);
                this.providerStatus.set(updated.providerStatus);
            }
            this.messageService.add({
                severity: 'success',
                summary: 'Saved',
                detail: 'Settings saved successfully.',
            });
            return true;
        } finally {
            this.saving.set(false);
        }
    }

    // ── Plain navigation between post-creation steps ────────────────────────

    protected goTo(step: number): void {
        if (this.saving() || this.creating()) return;
        this.stepError.set(null);
        this.step.set(step);
    }

    // ── Done ────────────────────────────────────────────────────────────────

    protected openFullSettings(): void {
        const realm = this.realm();
        if (realm) {
            this.router.navigate(['/website', realm.RealmId]);
        }
    }

    protected backToList(): void {
        this.router.navigate(['/website']);
    }
}

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}

function emptyBranding(): RealmBrandingConfig {
    return {
        Name: '',
        FullName: '',
        Primary: '',
        Secondary: '',
        DisplayName: '',
        Description: '',
        FaviconUrl: null,
        OgImageUrl: null,
        LogoIconUrl: null,
    };
}

function emptyFinera(): RealmFineraConfig {
    return {
        IsEnabled: false,
        BaseUrl: '',
        Deposit: { GroupId: '', ControlKey: '' },
    };
}

function emptyFineraWithdraw(): RealmFineraWithdrawConfig {
    return {
        GroupId: '',
        ControlKey: '',
        ConnectingPartyName: '',
        RsaPrivateKeyPem: '',
    };
}
